import {
    MapperKeys,
    transformSourceToTargetKeys,
} from '@common/utils/query/dataMappers';
import { mergeWithDefault } from '@common/utils/mergeWithDefault';

import {
    BackendFrameRate,
    BackendMediaObjectInstance,
    BackendTrack,
    DEFAULT_FRAME_RATE,
    DEFAULT_MEDIA_IDENTIFIER,
    FrameRate,
    MEDIA_IDENTIFIER_NAMES,
    MediaIdentifier,
    MediaIdentifiers,
    MediaObjectQuality,
    MediaObjectType,
    Track,
} from '../types/media';

type ExtendedBackendMOI = Omit<BackendMediaObjectInstance, 'mediaInfo'> & {
    version?: number | string;
    mediaInfo?: NonNullable<BackendMediaObjectInstance['mediaInfo']> & {
        fileName?: string;
    };
};

// --- Shared Utilities ---

export const mapFrameRate = (
    backendFrameRate?: BackendFrameRate,
): FrameRate => {
    if (!backendFrameRate) return DEFAULT_FRAME_RATE;
    const { numerator, denumerator, drop_frame } = backendFrameRate;

    return mergeWithDefault(
        {
            denumerator,
            numerator,
            dropFrames: drop_frame,
            fps: `${numerator}/${denumerator}`,
        },
        DEFAULT_FRAME_RATE,
    );
};

export const mapAspectRatio = (
    tracks?: BackendTrack[],
): { width: number; height: number } => {
    const videoTrack = tracks?.find((t) => t.type === 'VIDEO');
    return {
        width: videoTrack?.width ?? 1920,
        height: videoTrack?.height ?? 1080,
    };
};

const createEmptyMediaIdentifier = (
    type: MediaObjectType,
    quality: MediaObjectQuality,
): MediaIdentifier => ({ ...DEFAULT_MEDIA_IDENTIFIER, type, quality });

export const getDefaultMediaIdentifiers = (): MediaIdentifiers => {
    return Object.entries(MEDIA_IDENTIFIER_NAMES).reduce((acc, [key]) => {
        const type = key as MediaObjectType;
        acc[type] = {
            LOW: createEmptyMediaIdentifier(type, 'LOW'),
            MEDIUM: createEmptyMediaIdentifier(type, 'MEDIUM'),
            HIGH: createEmptyMediaIdentifier(type, 'HIGH'),
            ULTRA: createEmptyMediaIdentifier(type, 'ULTRA'),
        };
        return acc;
    }, {} as MediaIdentifiers);
};

const resolveCdnUrl = (
    originalHref: string,
    mimeType: string,
    version?: string | number,
    fileName?: string,
): string => {
    if (!fileName || !version) return originalHref;

    if (mimeType === 'application/dash+xml') {
        const cdnBase = originalHref.replace(
            'platform.limecraft.com/api',
            'media.limecraft.com/files',
        );
        const safeFileName = fileName.split(/[/\\]/).pop() || '';
        const targetFileName = !safeFileName.endsWith('.mpd')
            ? `${safeFileName}.mpd`
            : safeFileName;
        return `${cdnBase}/v/${version}/stream/${encodeURIComponent(targetFileName)}`;
    }

    return originalHref;
};

export const mapDirectMediaIdentifier = (
    instance: ExtendedBackendMOI,
): MediaIdentifier => {
    const type = instance.type as MediaObjectType;
    const quality = (instance.quality || 'LOW') as MediaObjectQuality;
    const { duration = 0, frames = 0, fileName } = instance.mediaInfo ?? {};

    return {
        ...createEmptyMediaIdentifier(type, quality),
        id: String(instance.id),
        mediaObjectId: String(instance.mediaObjectId),
        mimeType: instance.mimeType,
        href: resolveCdnUrl(
            instance.href,
            instance.mimeType,
            instance.version,
            fileName,
        ),
        duration,
        frames,
        frameRate: mapFrameRate(
            instance.mediaInfo?.sampleRate as BackendFrameRate,
        ),
        ...mapAspectRatio(instance.mediaInfo?.tracks),
    };
};

export const mapDirectMediaIdentifiers = (
    instances: BackendMediaObjectInstance[] = [],
): MediaIdentifiers => {
    return instances
        .filter((instance) => {
            if (!instance.materialInfo) return true;
            return (
                instance.materialInfo.onFlow === true ||
                instance.materialInfo.online === true
            );
        })
        .reduce((acc, instance) => {
            const mapped = mapDirectMediaIdentifier(instance);
            if (acc[mapped.type]) {
                acc[mapped.type][mapped.quality] = mapped;
            }
            return acc;
        }, getDefaultMediaIdentifiers());
};

const parseMOI = (infoStr: string) => {
    try {
        return JSON.parse(infoStr);
    } catch {
        return null;
    }
};

export const mapSearchMediaIdentifiers = (
    mediaObjectId: string | number,
    moiInfo: string[] = [],
    availableFlags: string[] | string = [],
): MediaIdentifiers => {
    const flagsArray = Array.isArray(availableFlags)
        ? availableFlags
        : [availableFlags];

    const activeKeys = new Set(
        flagsArray.map((flag) => {
            const [type, _profile, quality] = String(flag).split(';');
            return `${type}:${quality}`;
        }),
    );

    return moiInfo
        .map(parseMOI)
        .filter((inst) => {
            if (!inst) return false;

            const isAvailable = activeKeys.has(`${inst.type}:${inst.quality}`);
            return isAvailable;
        })
        .reduce((acc, inst) => {
            const type = inst.type as MediaObjectType;
            const quality = inst.quality as MediaObjectQuality;

            if (acc[type]) {
                acc[type][quality] = {
                    ...createEmptyMediaIdentifier(type, quality),
                    id: String(inst.id),
                    mediaObjectId: String(mediaObjectId),
                    mimeType: inst.mimetype,
                    width: inst.width || 0,
                    height: inst.height || 0,
                };
            }
            return acc;
        }, getDefaultMediaIdentifiers());
};

// --- Track Mapper ---

const TRACK_KEYS: MapperKeys<BackendTrack, Track> = {
    id: 'id',
    type: 'type',
    start: 'start',
    end: 'end',
    duration: 'duration',
    stitchCount: 'stitchCount',
    height: 'height',
    width: 'width',
    stitchX: 'stitchX',
    stitchY: 'stitchY',
    src: 'number',
    frameRate: 'number',
    mediaObjectId: 'mediaObjectTrackId',
    moiId: 'mediaObjectInstanceId',
    productionId: 'number',
};

export const mapBackendTracksToFrontend = (raw: BackendTrack): Track => {
    const transformed = transformSourceToTargetKeys(raw, TRACK_KEYS);
    const { denumerator, numerator, drop_frame } = raw.fps;
    const frameRate: FrameRate = {
        denumerator,
        numerator,
        dropFrames: drop_frame,
        fps: `${numerator}/${denumerator}`,
    };

    return {
        ...transformed,
        src: raw.hrefs.data,
        frameRate,
    };
};
